import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "react-oidc-context";

interface Ctx {
  tenant: string | null;
  setTenant: (t: string | null) => void;
  tenants: string[];
  isInternal: boolean;
}

const TenantCtx = createContext<Ctx>({
  tenant: null,
  setTenant: () => {},
  tenants: [],
  isInternal: false,
});

const STORAGE_KEY = "platform-v2:tenant";

function extractTenants(groups: string[]): string[] {
  return Array.from(
    new Set(
      groups
        .filter((g) => g.startsWith("/tenants/"))
        .map((g) => g.slice("/tenants/".length).split("/")[0])
        .filter((t) => t && t !== "internal"),
    ),
  ).sort();
}

export function TenantProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const groups = (auth.user?.profile as any)?.groups ?? [];
  const claimedTenant = (auth.user?.profile as any)?.tenant_id ?? null;
  const isInternal = groups.includes("/tenants/internal");
  const token = auth.user?.access_token;

  // O time interno nao tem grupo por cliente: a lista vem da API.
  const [internalTenants, setInternalTenants] = useState<string[]>([]);
  useEffect(() => {
    if (!isInternal || !token) return;
    fetch("/api/tenants", { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" } })
      .then((r) => (r.ok ? r.json() : { tenants: [] }))
      .then((b) => setInternalTenants((b.tenants ?? []).filter((t: string) => t !== "internal").sort()))
      .catch(() => setInternalTenants([]));
  }, [isInternal, token]);

  const tenants = isInternal ? internalTenants : extractTenants(groups);

  const [tenant, setTenantState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });

  // Semeia o tenant padrao, e descarta o salvo quando nao pertence a quem entrou:
  // o localStorage sobrevive a troca de usuario no mesmo navegador.
  useEffect(() => {
    if (tenants.length === 0) return;
    if (!tenant || !tenants.includes(tenant)) {
      const seed = tenants.includes(claimedTenant) ? claimedTenant : tenants[0];
      setTenantState(seed);
    }
  }, [tenant, claimedTenant, tenants.join("|")]);

  useEffect(() => {
    if (tenant) localStorage.setItem(STORAGE_KEY, tenant);
    else localStorage.removeItem(STORAGE_KEY);
  }, [tenant]);

  function setTenant(t: string | null) {
    setTenantState(t);
  }

  // enquanto a lista nao chegou, nao expoe um tenant que pode ser de outra pessoa
  const valid = tenant && tenants.includes(tenant) ? tenant : null;

  return (
    <TenantCtx.Provider value={{ tenant: valid, setTenant, tenants, isInternal }}>
      {children}
    </TenantCtx.Provider>
  );
}

export function useTenant() {
  return useContext(TenantCtx);
}
